/**
 * Fallback storage strategy for cart data using localStorage
 * This provides a backup when IndexedDB is unavailable or fails
 */

import type { GetFullCartQuery } from "@src/entities/types/api"

export interface CartStorageFallback {
  cart: GetFullCartQuery["cart"] | null
  timestamp: number
  version: string
}

const CART_FALLBACK_KEY = 'onyx-rose-cart-fallback'
const CART_FALLBACK_VERSION = '1.0'
const FALLBACK_EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours

/**
 * Clears cart data from localStorage fallback
 */
export function clearCartFromLocalStorage(): void {
  try {
    if (typeof window === 'undefined') return
    localStorage.removeItem(CART_FALLBACK_KEY)
  } catch (error) {
    console.warn('Failed to clear cart fallback from localStorage:', error)
  }
}

/**
 * Saves cart data to localStorage as a fallback
 */
export function saveCartToLocalStorage(cart: GetFullCartQuery["cart"] | null): void {
  try {
    if (typeof window === 'undefined') return

    const fallbackData: CartStorageFallback = {
      cart,
      timestamp: Date.now(),
      version: CART_FALLBACK_VERSION
    }

    localStorage.setItem(CART_FALLBACK_KEY, JSON.stringify(fallbackData))
  } catch (error) {
    console.warn('Failed to save cart fallback to localStorage:', error)
  }
}

/**
 * Retrieves cart data from localStorage fallback
 */
export function getCartFromLocalStorage(): GetFullCartQuery["cart"] | null {
  try {
    if (typeof window === 'undefined') return null

    const stored = localStorage.getItem(CART_FALLBACK_KEY)
    if (!stored) return null

    const fallbackData: CartStorageFallback = JSON.parse(stored)

    // Check if data is expired
    if (Date.now() - fallbackData.timestamp > FALLBACK_EXPIRY_MS) {
      clearCartFromLocalStorage()
      return null
    }

    // Check version compatibility
    if (fallbackData.version !== CART_FALLBACK_VERSION) {
      clearCartFromLocalStorage()
      return null
    }

    return fallbackData.cart
  } catch (error) {
    console.warn('Failed to retrieve cart fallback from localStorage:', error)
    clearCartFromLocalStorage()
    return null
  }
}

/**
 * Updates cart item count in localStorage fallback
 */
export function updateCartCountInLocalStorage(): void {
  try {
    const existingCart = getCartFromLocalStorage()
    if (existingCart) {
      saveCartToLocalStorage({
        ...existingCart,
        // Update the total count if available
        lineItems: existingCart.lineItems?.map(item => ({
          ...item,
          // We can't accurately update individual quantities in fallback
          // This is just a rough estimate for display purposes
        }))
      })
    }
  } catch (error) {
    console.warn('Failed to update cart count in localStorage fallback:', error)
  }
}

/**
 * Enhanced getCart function that tries localStorage fallback on failure
 */
export async function getCartWithFallback<T>(
  getCartFn: () => Promise<{ data?: T; errors?: any[] | undefined }>
): Promise<{ data?: T; errors?: any[]; fromFallback?: boolean }> {
  try {
    const result = await getCartFn()

    // If successful, save to fallback storage
    if (result.data && !result.errors) {
      // Extract cart data if this is a cart response
      const cartData = (result.data as any)?.cart
      if (cartData) {
        saveCartToLocalStorage(cartData)
      }
    }

    return result
  } catch (error) {
    console.warn('Cart fetch failed, trying localStorage fallback:', error)

    // Try localStorage fallback
    const fallbackCart = getCartFromLocalStorage()
    if (fallbackCart) {
      return {
        data: { cart: fallbackCart } as T,
        errors: undefined,
        fromFallback: true
      }
    }

    // No fallback available, return original error
    throw error
  }
}

/**
 * Checks if fallback storage is available and not full
 */
export function isFallbackStorageAvailable(): boolean {
  try {
    if (typeof window === 'undefined') return false

    // Test if we can write to localStorage
    const testKey = 'storage-test'
    localStorage.setItem(testKey, 'test')
    localStorage.removeItem(testKey)

    return true
  } catch {
    return false
  }
}

